import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../../../app/hooks';
import { setSelectedConversation, resetChatView } from '../../../../../../../../store/dashboard/askAica/actions';
import { ConversationListItem } from '../../../../../../../../store/dashboard/askAica/types';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../../../utils/dateUtils';
import { getPlatformUserId } from '../../../../../../../../utils/utils';
import CollapsePanelIcon from './CollapsePanelIcon';
import styles from './ConversationSidebar.module.scss';

interface ConversationSectionProps {
    title: string;
    items: ConversationListItem[];
    selectedConversationId: string | null;
    onRowClick: (id: string) => void;
    emptyHint: string;
    viewOnlyBadge?: boolean;
    loggedInUserName?: string;
}

const ConversationSection: React.FC<ConversationSectionProps> = ({
    title,
    items,
    selectedConversationId,
    onRowClick,
    emptyHint,
    viewOnlyBadge,
    loggedInUserName,
}) => (
    <div className={styles.Section}>
        <div className={styles.SectionHeaderRow}>
            <span className={styles.SectionHeader}>{title}</span>
            {viewOnlyBadge && <span className={styles.ViewOnlyBadge}>VIEW ONLY</span>}
        </div>
        {items.length === 0 ? (
            <span className={styles.EmptyHint}>{emptyHint}</span>
        ) : (
            items.map((c: ConversationListItem) => (
                <ConversationRow
                    key={c.conversation_id}
                    conversationId={c.conversation_id}
                    title={c.title}
                    userName={c.user_name}
                    lastMessageAt={c.last_message_at}
                    isSelected={selectedConversationId === c.conversation_id}
                    onClick={onRowClick}
                    loggedInUserName={loggedInUserName}
                />
            ))
        )}
    </div>
);

// Extracted sub-component so the renderer is defined once and used in both
// .map() calls — avoids an in-body function re-created on every render.
interface ConversationRowProps {
    conversationId: string;
    title: string | null;
    userName: string | null;
    lastMessageAt: string | null;
    isSelected: boolean;
    onClick: (id: string) => void;
    /** When true, fall back to the logged-in user's display name before "Unknown user" */
    loggedInUserName?: string;
}

const ConversationRow: React.FC<ConversationRowProps> = ({
    conversationId,
    title,
    userName,
    lastMessageAt,
    isSelected,
    onClick,
    loggedInUserName,
}) => {
    const displayTitle = title || 'Untitled';
    // FIX 5: own rows use the Redux display name when the backend user_name is null
    const displayName = userName || loggedInUserName || 'Unknown user';
    const timestamp = formatDateByFormat(lastMessageAt ?? undefined, DATE_FORMATS.LIBRARY_FILE_TIMESTAMP);

    return (
        <button className={`${styles.Row} ${isSelected ? styles.RowSelected : ''}`} onClick={() => onClick(conversationId)}>
            <span className={styles.RowTitle}>{displayTitle}</span>
            <span className={styles.RowSubtitle}>
                {displayName}
                {timestamp && (
                    <>
                        <span className={styles.RowDot}>•</span>
                        {timestamp}
                    </>
                )}
            </span>
        </button>
    );
};

interface ConversationSidebarProps {
    fetchChatHistory: (isInitialFetch?: boolean, conversationId?: string | null, reset?: boolean) => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({ fetchChatHistory }) => {
    const dispatch = useAppDispatch();
    const [collapsed, setCollapsed] = useState(false);

    const currentUserName = useAppSelector((state) => state.user.userData.userName);
    const { conversations, isLoadingConversations, selectedConversationId } = useAppSelector((state) => state.dashboard.askAicaReducer);

    const platformUserId = getPlatformUserId();
    const mine = conversations.filter((c: ConversationListItem) => c.user_id === platformUserId);
    const team = conversations.filter((c: ConversationListItem) => c.user_id !== platformUserId);

    // True when the user is viewing a teammate's (view-only) conversation.
    const viewingTeamConversation =
        !!selectedConversationId && team.some((c: ConversationListItem) => c.conversation_id === selectedConversationId);

    const handleRowClick = (conversationId: string) => {
        // Reset chat view BEFORE switching — clears messages/pagination so
        // initMessageThread does a clean replace, not an interleave. Also
        // ensures fetchChatHistory uses page=1 (reset=true) not the old page.
        dispatch(resetChatView());
        dispatch(setSelectedConversation(conversationId));
        fetchChatHistory(false, conversationId, true);
    };

    const handleBackToOwn = () => {
        dispatch(resetChatView());
        dispatch(setSelectedConversation(null));
        // Pass null (not undefined) so fetchChatHistory's effective-id logic treats
        // this as an explicit "no conversation id" — guards against the hook's
        // closure still seeing the old selectedConversationId value.
        fetchChatHistory(false, null, true);
    };

    if (collapsed) {
        return (
            <div className={styles.SidebarCollapsed}>
                {/* FIX 2: expand button — mirrored collapse icon */}
                <button className={styles.CollapseBtn} onClick={() => setCollapsed(false)} title="Expand conversations">
                    <span className={styles.MirroredIcon}>
                        <CollapsePanelIcon color="#6021b3" width="18" height="18" />
                    </span>
                </button>
            </div>
        );
    }

    return (
        <div className={styles.Sidebar}>
            {/* Top row: "Back to my chat" (left, only while viewing a teammate's chat) + collapse (right). */}
            <div className={styles.TopRow}>
                {viewingTeamConversation ? (
                    <button className={styles.BackToOwnBtn} onClick={handleBackToOwn}>
                        <span className={styles.BackToOwnIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="12" fill="var(--primary-purple)" />
                                <path
                                    d="M15.5 12H8M11 8.5L7.5 12L11 15.5"
                                    stroke="#ffffff"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        Back to my chat
                    </button>
                ) : (
                    <span />
                )}
                <button className={styles.CollapseBtn} onClick={() => setCollapsed(true)} title="Collapse conversations">
                    <CollapsePanelIcon color="#6b7280" width="18" height="18" />
                </button>
            </div>

            {isLoadingConversations ? (
                <div className={styles.Shimmer}>
                    {[1, 2, 3].map((i) => (
                        <div key={i} className={styles.ShimmerRow} />
                    ))}
                </div>
            ) : (
                <>
                    <ConversationSection
                        title="Your Conversations"
                        items={mine}
                        selectedConversationId={selectedConversationId}
                        onRowClick={handleRowClick}
                        emptyHint="No conversations yet"
                        loggedInUserName={currentUserName}
                    />

                    <ConversationSection
                        title="Team's Conversations"
                        items={team}
                        selectedConversationId={selectedConversationId}
                        onRowClick={handleRowClick}
                        emptyHint="No team conversations"
                        viewOnlyBadge
                    />
                </>
            )}
        </div>
    );
};

export default ConversationSidebar;
